import { Platform } from 'react-native';

import { formatInTimeZone } from 'date-fns-tz';

import { AddressTypes, type AddressType } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

/**
 * Returns the URL for the Momo survey.
 * Note that the `cels-4626-momo-rate-order-survey-enabled` flag contains either "Disabled" or the survey URL.
 * @see {@link https://go-momos.notion.site/Survey-URL-Query-Params-1ecef141e357801e8bb2c428c5b36755}
 */
export const useMomoSurveyUrl = (
  customer?: MomoSurveyUrlCustomer | null,
  order?: MomoSurveyUrlOrder | null,
) => {
  const momoSurveyUrl = useFeatureFlag('cels-4626-momo-rate-order-survey-enabled'); // prettier-ignore
  const restaurantEntityIdMapping = useFeatureFlag('cels-4651-momo-poc-restaurant-entity-id-mapping'); // prettier-ignore

  if (momoSurveyUrl === disabled || !order) return null;

  const { orderId, orderTime, orderType, restaurantEntityId } = order;

  const outletId = restaurantEntityIdMapping?.[String(restaurantEntityId ?? '')]; // prettier-ignore
  const serviceType = orderType ? orderTypeToServiceType(orderType) : null;

  if (!outletId || !orderId || !orderTime || !serviceType) {
    return null;
  }

  try {
    const url = new URL(momoSurveyUrl);
    url.searchParams.set('serviceType', serviceType);
    url.searchParams.set('outletId', outletId);
    url.searchParams.set('name', getCustomerName(customer?.firstName, customer?.lastName));
    url.searchParams.set('email', customer?.email ?? '');
    url.searchParams.set('phoneNumber', customer?.phoneNumber ?? '');
    url.searchParams.set('orderId', orderId);
    url.searchParams.set('orderTime', toMomoTimestamp(orderTime));
    url.searchParams.set('momosChannel', Platform.OS === 'web' ? 'web' : 'app');

    return url.toString();
  } catch {
    return null;
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getCustomerName = (firstName?: string | null, lastName?: string | null) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim();

const orderTypeToServiceType = (orderType: AddressType): MomosServiceType | null => {
  switch (orderType) {
    case AddressTypes.pickup:
      return 'pickup';
    case AddressTypes.delivery:
      return 'delivery';
    case AddressTypes.kiosk:
    case AddressTypes['in-store']:
      return 'dinein';
    case AddressTypes.outpost:
      return 'outpost';
    default:
      return null;
  }
};

/**
 * Date and time of the order in the format YYYY-MM-DDThh24:mi:ssZ
 */
export function toMomoTimestamp(date: string) {
  return formatInTimeZone(new Date(date), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

// ─── Constants ───────────────────────────────────────────────────────────────

const disabled = 'Disabled' as const;

// ─── Types ───────────────────────────────────────────────────────────────────

type MomosServiceType = 'dinein' | 'pickup' | 'delivery' | 'outpost';

type MomoSurveyUrlCustomer = {
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

type MomoSurveyUrlOrder = {
  orderId?: string | null;
  orderTime?: string | null;
  orderType?: AddressType | null;
  restaurantEntityId?: string | number | null;
};
